import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { AmbienceStyle } from '@/lib/ambience';

export interface AmbiencePrefs {
  enabled: boolean;
  style: AmbienceStyle;
  volume: number;
}

const DEFAULTS: AmbiencePrefs = {
  enabled: false,
  style: 'synth',
  volume: 0.3,
};

export function useAmbiencePreferences() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState<AmbiencePrefs>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setPrefs(DEFAULTS);
      setLoading(false);
      return;
    }
    let cancelled = false;
    supabase
      .from('user_preferences')
      .select('ambience_enabled, ambience_style, ambience_volume')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setPrefs({
          enabled: Boolean(data?.ambience_enabled),
          style: (data?.ambience_style as AmbienceStyle) ?? DEFAULTS.style,
          volume: typeof data?.ambience_volume === 'number' ? data.ambience_volume : DEFAULTS.volume,
        });
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const update = useCallback(
    async (patch: Partial<AmbiencePrefs>) => {
      const next = { ...prefs, ...patch };
      setPrefs(next);
      if (!user?.id) return;
      const dbPatch: Record<string, any> = {};
      if (patch.enabled !== undefined) dbPatch.ambience_enabled = next.enabled;
      if (patch.style !== undefined) dbPatch.ambience_style = next.style;
      if (patch.volume !== undefined) dbPatch.ambience_volume = next.volume;
      // We update an existing user_preferences row; if there isn't one (very
      // rare for an authenticated user past onboarding) we fall back to upsert.
      const { error } = await supabase
        .from('user_preferences')
        .update(dbPatch)
        .eq('user_id', user.id);
      if (error) {
        // Silent — the in-memory state is already updated; persistence will
        // retry on next change or page load.
        console.warn('ambience pref save failed:', error.message);
      }
    },
    [prefs, user?.id],
  );

  return { prefs, loading, update };
}
